/**
 * Geo Location Popup
 * A simple custom element for market/country selection
 */

class GeoLocationPopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.focusableElements = [];
    this.lastFocusedElement = null;
    this.autoShowTimeout = null;
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();

    if (this.isCustomizer) {
      this.bindCustomizerEvents();
    } else {
      this.checkAutoShow();
    }
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.unbindCustomizerEvents();

    if (this.autoShowTimeout) {
      clearTimeout(this.autoShowTimeout);
      this.autoShowTimeout = null;
    }

    if (this.isOpen) {
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // Configuration getters
  // ============================================

  get autoShowEnabled() {
    return this.dataset.autoShow === 'true';
  }

  get showOnce() {
    return this.dataset.showOnce === 'true';
  }

  get delay() {
    return parseInt(this.dataset.delay || '0', 10) * 1000;
  }

  get storage() {
    return this.showOnce ? localStorage : sessionStorage;
  }

  get storageKey() {
    return 'geo-popup-shown';
  }

  get isCustomizer() {
    return this.dataset.customizer === 'true';
  }

  // ============================================
  // DOM caching
  // ============================================

  cacheElements() {
    this.overlay = this.querySelector('[data-overlay]');
    this.dialog = this.querySelector('[role="dialog"]');
    this.closeButtons = this.querySelectorAll('[data-close]');
    this.marketButtons = this.querySelectorAll('[data-market-button]');
    this.form = this.querySelector('#geo-localization-form');
    this.countryInput = this.querySelector('[data-country-input]');
    this.languageInput = this.querySelector('[data-language-input]');
  }

  // ============================================
  // Event binding
  // ============================================

  bindEvents() {
    // Close buttons
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', this.handleClose);
    });

    // Overlay click
    this.overlay?.addEventListener('click', this.handleClose);

    // Escape key
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);

    // Market selection buttons (internal markets)
    this.marketButtons.forEach(btn => {
      btn.addEventListener('click', this.handleMarketSelect);
    });

    // Manual trigger buttons (header country selector) - use event delegation
    // This handles triggers that may be added to DOM after this element connects
    this.boundHandleTriggerClick = this.handleTriggerClick.bind(this);
    document.addEventListener('click', this.boundHandleTriggerClick);
  }

  unbindEvents() {
    // Close buttons
    this.closeButtons.forEach(btn => {
      btn.removeEventListener('click', this.handleClose);
    });

    // Overlay
    this.overlay?.removeEventListener('click', this.handleClose);

    // Escape key
    document.removeEventListener('keydown', this.boundHandleKeydown);

    // Market buttons
    this.marketButtons.forEach(btn => {
      btn.removeEventListener('click', this.handleMarketSelect);
    });

    // Trigger buttons (event delegation)
    document.removeEventListener('click', this.boundHandleTriggerClick);
  }

  // ============================================
  // Customizer events (section select/deselect)
  // ============================================

  bindCustomizerEvents() {
    this.shopifySection = this.closest('.shopify-section');
    if (!this.shopifySection) return;

    this.boundOnSectionSelect = (e) => this.open(!e.detail.load);
    this.boundOnSectionDeselect = () => this.close();

    this.shopifySection.addEventListener('shopify:section:select', this.boundOnSectionSelect);
    this.shopifySection.addEventListener('shopify:section:deselect', this.boundOnSectionDeselect);
  }

  unbindCustomizerEvents() {
    if (!this.shopifySection) return;
    this.shopifySection.removeEventListener('shopify:section:select', this.boundOnSectionSelect);
    this.shopifySection.removeEventListener('shopify:section:deselect', this.boundOnSectionDeselect);
  }

  // ============================================
  // Event handlers (arrow functions for correct `this`)
  // ============================================

  handleClose = () => {
    this.close();
    // Don't save to storage in customizer
    if (!this.isCustomizer) {
      this.markAsShown();
    }
  };

  handleKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleClose();
      return;
    }

    // Focus trap
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  handleMarketSelect = (event) => {
    // Don't submit in customizer
    if (this.isCustomizer) {
      return;
    }

    const button = event.currentTarget;
    const countryCode = button.dataset.country;
    const languageCode = button.dataset.language;

    if (this.form && this.countryInput && this.languageInput) {
      this.countryInput.value = countryCode;
      this.languageInput.value = languageCode;
      this.markAsShown();
      this.form.submit();
    }
  };

  handleTriggerClick(event) {
    // Event delegation - check if click was on a trigger button or its children
    const trigger = event.target.closest('.btn-geo-location-trigger');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    this.open();
  }

  // ============================================
  // Auto-show logic (uses Shopify's client-side API)
  // ============================================

  async checkAutoShow() {
    if (!this.autoShowEnabled) return;
    if (this.hasBeenShown()) return;

    // Fetch detected country from Shopify's client-side API
    const detectedCountry = await this.getDetectedCountry();
    const currentCountry = this.dataset.currentCountry;

    // No detection or already on correct market
    if (!detectedCountry || detectedCountry === currentCountry) return;

    // Country mismatch - show popup after delay
    this.autoShowTimeout = setTimeout(() => {
      this.autoShowTimeout = null;
      if (this.isConnected && !this.hasBeenShown()) {
        this.open();
        this.markAsShown();
      }
    }, this.delay);
  }

  async getDetectedCountry() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'browsing_context_suggestions.json');
      const data = await response.json();
      return data.detected_values?.country?.handle?.toUpperCase() || null;
    } catch {
      return null;
    }
  }

  // ============================================
  // Storage helpers
  // ============================================

  hasBeenShown() {
    return this.storage.getItem(this.storageKey) === 'true';
  }

  markAsShown() {
    this.storage.setItem(this.storageKey, 'true');
  }

  // ============================================
  // Open / Close
  // ============================================

  open(animate = true) {
    if (this.isOpen) return;

    if (!animate) {
      this.classList.add('geo-popup--instant');
    }

    this.lastFocusedElement = document.activeElement;
    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (!animate) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.remove('geo-popup--instant'));
      });
    }

    requestAnimationFrame(() => {
      this.updateFocusableElements();
      if (this.focusableElements.length > 0) {
        this.focusableElements[0].focus();
      }
    });

    this.dispatchEvent(new CustomEvent('geo-popup:open'));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    document.body.style.overflow = '';

    // Restore focus
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }

    this.dispatchEvent(new CustomEvent('geo-popup:close'));
  }

  // ============================================
  // Focus management
  // ============================================

  updateFocusableElements() {
    if (!this.dialog) {
      this.focusableElements = [];
      return;
    }

    const selectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];

    this.focusableElements = Array.from(
      this.dialog.querySelectorAll(selectors.join(','))
    ).filter(el => el.offsetParent !== null);
  }

  trapFocus(event) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }
}

// Register custom element
if (!customElements.get('geo-location-popup')) {
  customElements.define('geo-location-popup', GeoLocationPopup);
}
